/**
 * TanStack Query hooks for the operational map page.
 *
 * The single endpoint returns three buckets of coordinates (merchants,
 * couriers, active orders) in one shot, so this module is intentionally
 * thin — we just typed-fetch the response and let the UI decide which
 * buckets to render based on the legend toggles.
 *
 * The query refetches every 30s while the page is mounted so the map stays
 * close to live without a websocket connection.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';

export type MapData = components['schemas']['MapDataResponse'];

const REFETCH_INTERVAL_MS = 30_000;

export const mapKeys = {
  all: ['map'] as const,
  entities: () => [...mapKeys.all, 'entities'] as const,
};

/**
 * Fetches `merchants`, `couriers` and `activeOrders` in a single call.
 *
 * The endpoint is fully typed in the generated OpenAPI surface, so we go
 * through the typed `api` client instead of a raw `fetch`.
 */
export function useMapEntities(): UseQueryResult<MapData, Error> {
  return useQuery<MapData, Error>({
    queryKey: mapKeys.entities(),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/map/entities', {});
      if (error) throw error as Error;
      return (data ?? {}) as MapData;
    },
    refetchInterval: REFETCH_INTERVAL_MS,
    staleTime: REFETCH_INTERVAL_MS,
  });
}
